// API client utilities
const API_BASE = import.meta.env.VITE_API_URL || '/api'

export type PaginatedResponse<T> = {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
  filters?: Record<string, string>
}

export type PaginationParams = {
  page?: number
  limit?: number
}

export type ListParams = PaginationParams & {
  filters?: Record<string, string>
}

export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include', // Include cookies for auth
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  // Handle 401 - redirect to login
  if (response.status === 401) {
    // Only redirect if not already on auth pages
    if (!window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/register') &&
        !window.location.pathname.startsWith('/verify-2fa')) {
      window.location.href = '/login'
    }
    const error = await response.json().catch(() => ({ error: 'Authentication required' }))
    throw new Error(error.error || 'Authentication required')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }))
    // Handle Zod validation errors
    if (errorData.error?.issues) {
      const messages = errorData.error.issues.map((i: any) => i.message).join(', ')
      throw new Error(messages)
    }
    throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`)
  }

  if (response.status === 204) {
    return null as T
  }

  return response.json()
}

// Generic CRUD operations
export function createCrudApi<T, TInput>(endpoint: string) {
  return {
    list: (params?: ListParams) => {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.limit) searchParams.set('limit', params.limit.toString())
      if (params?.filters) {
        for (const [key, value] of Object.entries(params.filters)) {
          if (value) searchParams.set(key, value)
        }
      }
      const query = searchParams.toString()
      return apiRequest<PaginatedResponse<T>>(`/${endpoint}${query ? `?${query}` : ''}`)
    },
    get: (id: string) => apiRequest<T>(`/${endpoint}/${id}`),
    create: (data: TInput) =>
      apiRequest<T>(`/${endpoint}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<TInput>) =>
      apiRequest<T>(`/${endpoint}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      apiRequest<void>(`/${endpoint}/${id}`, {
        method: 'DELETE',
      }),
  }
}

// ===========================================
// WEB LEADS API
// ===========================================

export interface WebLeadListItem {
  id: string
  name: string
  company: string
  email: string
  phone: string | null
  interest: string
  message: string
  status: string
  viewedAt: string | null
  viewedById: string | null
  sourceUrl: string | null
  createdAt: string
  websiteConfigId: string
  websiteConfigName: string | null
}

export interface WebLeadDetail extends WebLeadListItem {
  // Additional contact info
  firstName: string | null
  lastName: string | null
  jobTitle: string | null

  // Inquiry
  privacyConsent: boolean
  formName: string | null

  // Source tracking
  referrerUrl: string | null
  ipAddress: string | null
  userAgent: string | null

  // UTM Attribution
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  utmContent: string | null
  utmTerm: string | null

  // Visitor Context
  country: string | null
  region: string | null
  city: string | null
  timezone: string | null
  browserLanguage: string | null

  // Device & Browser
  deviceType: string | null
  os: string | null
  browserName: string | null
  browserVersion: string | null
  screenResolution: string | null

  // Session Data
  sessionId: string | null
  entryPage: string | null
  pagesViewed: string[] | null
  pageCount: number | null
  timeOnSite: number | null
  returningVisitor: boolean | null

  // Consent
  analyticsConsent: boolean | null
  marketingConsent: boolean | null

  // Extended
  scrollDepth: number | null
  timeOnFormPage: number | null
  downloadRequested: string | null
  productInterest: string | null
  preferredContactMethod: string | null
  customFields: Record<string, unknown> | null

  // Conversion
  convertedToLeadId: string | null
  convertedAt: string | null
}

export interface WebLeadStats {
  unviewedCount: number
}

export const webLeadsApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<{ data: WebLeadListItem[], meta: { total: number, page: number, pageSize: number } }>(
      `/web-leads${query ? `?${query}` : ''}`
    )
  },
  get: (id: string) => apiRequest<WebLeadDetail>(`/web-leads/${id}`),
  getStats: () => apiRequest<WebLeadStats>('/web-leads/stats'),
  markViewed: (id: string) => apiRequest<WebLeadDetail>(`/web-leads/${id}/mark-viewed`, { method: 'PATCH' }),
  archive: (id: string) => apiRequest<WebLeadDetail>(`/web-leads/${id}/archive`, { method: 'PATCH' }),
  delete: (id: string) => apiRequest<void>(`/web-leads/${id}`, { method: 'DELETE' }),
}

// ===========================================
// WEBSITE CONFIGS API
// ===========================================

export interface WebsiteConfig {
  id: string
  name: string
  apiKeyPrefix: string
  allowedDomains: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface WebsiteConfigCreateResponse extends WebsiteConfig {
  apiKey: string
  message: string
}

export interface WebsiteConfigInput {
  name: string
  allowedDomains: string[]
  isActive?: boolean
}

export const websiteConfigsApi = {
  list: () => apiRequest<{ data: WebsiteConfig[] }>('/website-configs'),
  get: (id: string) => apiRequest<WebsiteConfig>(`/website-configs/${id}`),
  create: (data: WebsiteConfigInput) =>
    apiRequest<WebsiteConfigCreateResponse>('/website-configs', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<WebsiteConfigInput>) =>
    apiRequest<WebsiteConfig>(`/website-configs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  regenerateKey: (id: string) =>
    apiRequest<WebsiteConfigCreateResponse>(`/website-configs/${id}/regenerate-key`, {
      method: 'POST',
    }),
  delete: (id: string) => apiRequest<void>(`/website-configs/${id}`, { method: 'DELETE' }),
}

// ===========================================
// TICKETS API
// ===========================================

import type {
  TicketInput,
  TicketCommentInput,
  TicketWithRelations,
  TicketWithComments,
  TicketCommentWithAuthor,
  TicketStatus,
  TicketPriority,
  TicketCategory,
} from '../../shared/types'

export interface TicketStats {
  open: number
  in_progress: number
  pending: number
  resolved: number
  closed: number
  unassigned: number
}

export const ticketsApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<TicketWithRelations>>(`/tickets${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<TicketWithComments>(`/tickets/${id}`),
  create: (data: TicketInput) =>
    apiRequest<TicketWithRelations>('/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<TicketInput>) =>
    apiRequest<TicketWithRelations>(`/tickets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/tickets/${id}`, { method: 'DELETE' }),
  getComments: (id: string) => apiRequest<TicketCommentWithAuthor[]>(`/tickets/${id}/comments`),
  addComment: (id: string, data: TicketCommentInput) =>
    apiRequest<TicketCommentWithAuthor>(`/tickets/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getStats: (category?: TicketCategory) => {
    const query = category ? `?category=${category}` : ''
    return apiRequest<TicketStats>(`/tickets/stats/summary${query}`)
  },
}

export type { TicketInput, TicketCommentInput, TicketWithRelations, TicketWithComments, TicketCommentWithAuthor, TicketStatus, TicketPriority, TicketCategory }

// ===========================================
// ASSETS API (Service Hub)
// ===========================================

import type {
  AssetInput,
  AssetWithRelations,
  AssetStatus,
  AssetDocumentRecord,
  AssetBomItemRecord,
  AssetBomItemInput,
  TwinSampleDocument,
} from '../../shared/types'

export interface AssetStats {
  total: number
  active: number
  maintenance: number
  decommissioned: number
}

export const assetsApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<AssetWithRelations>>(`/assets${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<AssetWithRelations>(`/assets/${id}`),
  getBySerial: (serialNumber: string) => apiRequest<AssetWithRelations>(`/assets/serial/${encodeURIComponent(serialNumber)}`),
  create: (data: AssetInput) =>
    apiRequest<AssetWithRelations>('/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<AssetInput>) =>
    apiRequest<AssetWithRelations>(`/assets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/assets/${id}`, { method: 'DELETE' }),
  getStats: () => apiRequest<AssetStats>('/assets/stats'),
  // Documents
  getDocuments: (assetId: string) => apiRequest<AssetDocumentRecord[]>(`/assets/${assetId}/documents`),
  uploadDocument: (assetId: string, file: File, metadata: { title: string; documentType: string; language?: string }) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', metadata.title)
    formData.append('documentType', metadata.documentType)
    if (metadata.language) formData.append('language', metadata.language)
    return fetch(`${import.meta.env.VITE_API_URL || '/api'}/assets/${assetId}/documents`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(res => res.json())
  },
  deleteDocument: (assetId: string, docId: string) =>
    apiRequest<void>(`/assets/${assetId}/documents/${docId}`, { method: 'DELETE' }),
  getDocumentDownloadUrl: (assetId: string, docId: string) =>
    apiRequest<{ url: string }>(`/assets/${assetId}/documents/${docId}/download`),
  // BOM
  getBom: (assetId: string) => apiRequest<AssetBomItemRecord[]>(`/assets/${assetId}/bom`),
  updateBom: (assetId: string, items: AssetBomItemInput[]) =>
    apiRequest<AssetBomItemRecord[]>(`/assets/${assetId}/bom`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  updateBomItem: (assetId: string, bomItemId: string, data: { twinPartId: string | null }) =>
    apiRequest<AssetBomItemRecord>(`/assets/${assetId}/bom/${bomItemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteBomItem: (assetId: string, position: number) =>
    apiRequest<void>(`/assets/${assetId}/bom/${position}`, { method: 'DELETE' }),
  // History
  getHistory: (assetId: string, limit?: number) =>
    apiRequest<Array<{
      id: string
      caseNumber: string
      subject: string
      status: string
      priority: string
      category: string
      createdAt: string
      resolvedAt: string | null
      closedAt: string | null
    }>>(`/assets/${assetId}/history${limit ? `?limit=${limit}` : ''}`),
}

export type { AssetInput, AssetWithRelations, AssetStatus }

// ===========================================
// SERVICE CASES API (Service Hub)
// ===========================================

import type {
  ServiceCaseInput,
  ServiceCaseWithRelations,
  ServiceCaseWithComments,
  ServiceCaseCommentInput,
  ServiceCaseCommentWithAuthor,
  ServiceCaseStatus,
  ServiceCasePriority,
  ServiceCaseCategory,
} from '../../shared/types'

export interface ServiceCaseStats {
  total: number
  open: number
  in_progress: number
  waiting_customer: number
  waiting_parts: number
  resolved: number
  closed: number
  unassigned: number
}

export const serviceCasesApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<ServiceCaseWithRelations>>(`/service-cases${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<ServiceCaseWithComments>(`/service-cases/${id}`),
  getDetails: (id: string) => apiRequest<ServiceCaseWithComments>(`/service-cases/${id}/details`),
  create: (data: ServiceCaseInput) =>
    apiRequest<ServiceCaseWithRelations>('/service-cases', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<ServiceCaseInput>) =>
    apiRequest<ServiceCaseWithRelations>(`/service-cases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/service-cases/${id}`, { method: 'DELETE' }),
  assign: (id: string, assignedToId: string | null) =>
    apiRequest<ServiceCaseWithRelations>(`/service-cases/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assignedToId }),
    }),
  close: (id: string) =>
    apiRequest<ServiceCaseWithRelations>(`/service-cases/${id}/close`, { method: 'POST' }),
  resolve: (id: string) =>
    apiRequest<ServiceCaseWithRelations>(`/service-cases/${id}/resolve`, { method: 'POST' }),
  getStats: () => apiRequest<ServiceCaseStats>('/service-cases/stats'),
  // Comments
  addComment: (id: string, data: ServiceCaseCommentInput) =>
    apiRequest<ServiceCaseCommentWithAuthor>(`/service-cases/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

export type {
  ServiceCaseInput,
  ServiceCaseWithRelations,
  ServiceCaseWithComments,
  ServiceCaseCommentInput,
  ServiceCaseCommentWithAuthor,
  ServiceCaseStatus,
  ServiceCasePriority,
  ServiceCaseCategory,
}

// ===========================================
// TWIN ENGINE API
// ===========================================

export interface TwinProject {
  id: string
  name: string
  description?: string
  status: 'pending' | 'processing' | 'ready' | 'failed' | 'draft'
  modelUrl?: string
  metadata?: Record<string, unknown>
  createdAt: string
  updatedAt: string
  cadFilesCount?: number
  hasBom?: boolean
  matchRate?: number | null
}

// Transform Twin Engine response from snake_case to camelCase
function transformTwinProject(raw: Record<string, unknown>): TwinProject {
  return {
    id: raw.id as string,
    name: raw.name as string,
    description: raw.description as string | undefined,
    status: (raw.status as string) as TwinProject['status'],
    modelUrl: raw.model_url as string | undefined,
    metadata: raw.metadata as Record<string, unknown> | undefined,
    createdAt: raw.created_at as string,
    updatedAt: raw.updated_at as string,
    cadFilesCount: raw.cad_files_count as number | undefined,
    hasBom: raw.has_bom as boolean | undefined,
    matchRate: raw.match_rate as number | null | undefined,
  }
}

export interface TwinConversionStatus {
  projectId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress?: number
  message?: string
  startedAt?: string
  completedAt?: string
  error?: string
}

export interface TwinModelMetadata {
  partCount?: number
  parts?: Array<{
    id: string
    name: string
    partNumber?: string
  }>
  boundingBox?: {
    min: [number, number, number]
    max: [number, number, number]
  }
}

export interface TwinHealthStatus {
  status: 'ok' | 'degraded' | 'error'
  twinEngine: 'connected' | 'unhealthy' | 'unreachable'
  statusCode?: number
  error?: string
}

// ===========================================
// PRODUCTS API (CPQ Module)
// ===========================================

import type {
  ProductRecord,
  ProductWithPrices,
  ProductInput,
  ProductGroupRecord,
  ProductPriceRecord,
} from '../../shared/types'

export interface ProductStats {
  total: number
  active: number
  salesItems: number
  purchaseItems: number
  synced: number
  local: number
}

export const productsApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<ProductRecord>>(`/products${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<ProductRecord>(`/products/${id}`),
  getDetails: (id: string) => apiRequest<ProductWithPrices>(`/products/${id}/details`),
  getByCode: (itemCode: string) => apiRequest<ProductRecord>(`/products/code/${encodeURIComponent(itemCode)}`),
  search: (q: string, limit?: number) => {
    const params = new URLSearchParams({ q })
    if (limit) params.set('limit', limit.toString())
    return apiRequest<Array<{
      id: string
      itemCode: string
      itemName: string
      shortDescription: string | null
      defaultPrice: string | null
      defaultCurrency: string
      salesUnit: string
      valid: string
    }>>(`/products/search?${params.toString()}`)
  },
  create: (data: ProductInput) =>
    apiRequest<ProductRecord>('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<ProductInput>) =>
    apiRequest<ProductRecord>(`/products/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/products/${id}`, { method: 'DELETE' }),
  getStats: () => apiRequest<ProductStats>('/products/stats'),
  // Prices
  getPrices: (productId: string) => apiRequest<ProductPriceRecord[]>(`/products/${productId}/prices`),
  // Groups
  getGroups: () => apiRequest<ProductGroupRecord[]>('/products/groups'),
}

export type { ProductRecord, ProductWithPrices, ProductInput }

// ===========================================
// QUOTES API (CPQ Module)
// ===========================================

import type {
  QuoteRecord,
  QuoteWithRelations,
  QuoteInput,
  QuoteLineItemRecord,
  QuoteLineItemInput,
} from '../../shared/types'

export interface QuoteStats {
  total: number
  draft: number
  pending_approval: number
  approved: number
  sent: number
  accepted: number
  rejected: number
  expired: number
}

export const quotesApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<QuoteWithRelations>>(`/quotes${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<QuoteWithRelations>(`/quotes/${id}`),
  create: (data: QuoteInput) =>
    apiRequest<QuoteWithRelations>('/quotes', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<QuoteInput>) =>
    apiRequest<QuoteWithRelations>(`/quotes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/quotes/${id}`, { method: 'DELETE' }),
  submit: (id: string) =>
    apiRequest<QuoteWithRelations>(`/quotes/${id}/submit`, { method: 'POST' }),
  approve: (id: string) =>
    apiRequest<QuoteWithRelations>(`/quotes/${id}/approve`, { method: 'POST' }),
  reject: (id: string, reason?: string) =>
    apiRequest<QuoteWithRelations>(`/quotes/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  send: (id: string) =>
    apiRequest<QuoteWithRelations>(`/quotes/${id}/send`, { method: 'POST' }),
  accept: (id: string) =>
    apiRequest<QuoteWithRelations>(`/quotes/${id}/accept`, { method: 'POST' }),
  convert: (id: string) =>
    apiRequest<{ order: { id: string; orderNumber: string } }>(`/quotes/${id}/convert`, { method: 'POST' }),
  getStats: () => apiRequest<QuoteStats>('/quotes/stats'),
  // Line items
  addLineItem: (quoteId: string, data: Omit<QuoteLineItemInput, 'quoteId'>) =>
    apiRequest<QuoteLineItemRecord>(`/quotes/${quoteId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateLineItem: (quoteId: string, itemId: string, data: Partial<QuoteLineItemInput>) =>
    apiRequest<QuoteLineItemRecord>(`/quotes/${quoteId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteLineItem: (quoteId: string, itemId: string) =>
    apiRequest<void>(`/quotes/${quoteId}/items/${itemId}`, { method: 'DELETE' }),
}

export type { QuoteRecord, QuoteWithRelations, QuoteInput, QuoteLineItemRecord }

// ===========================================
// ORDERS API (CPQ Module)
// ===========================================

import type {
  OrderRecord,
  OrderWithRelations,
  OrderInput,
  OrderLineItemRecord,
} from '../../shared/types'

export interface OrderStats {
  total: number
  draft: number
  confirmed: number
  processing: number
  shipped: number
  delivered: number
  invoiced: number
  cancelled: number
}

export const ordersApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<OrderWithRelations>>(`/orders${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<OrderWithRelations>(`/orders/${id}`),
  create: (data: OrderInput) =>
    apiRequest<OrderWithRelations>('/orders', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<OrderInput>) =>
    apiRequest<OrderWithRelations>(`/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/orders/${id}`, { method: 'DELETE' }),
  confirm: (id: string) =>
    apiRequest<OrderWithRelations>(`/orders/${id}/confirm`, { method: 'POST' }),
  process: (id: string) =>
    apiRequest<OrderWithRelations>(`/orders/${id}/process`, { method: 'POST' }),
  ship: (id: string) =>
    apiRequest<OrderWithRelations>(`/orders/${id}/ship`, { method: 'POST' }),
  deliver: (id: string) =>
    apiRequest<OrderWithRelations>(`/orders/${id}/deliver`, { method: 'POST' }),
  invoice: (id: string) =>
    apiRequest<OrderWithRelations>(`/orders/${id}/invoice`, { method: 'POST' }),
  cancel: (id: string) =>
    apiRequest<OrderWithRelations>(`/orders/${id}/cancel`, { method: 'POST' }),
  getStats: () => apiRequest<OrderStats>('/orders/stats'),
}

export type { OrderRecord, OrderWithRelations, OrderInput, OrderLineItemRecord }

// ===========================================
// TWIN ENGINE API
// ===========================================

export const twinApi = {
  // Health check
  health: () => apiRequest<TwinHealthStatus>('/twin/health'),

  // Projects (Twin Engine returns paginated response with snake_case fields)
  listProjects: async (): Promise<TwinProject[]> => {
    const response = await apiRequest<{ data: Record<string, unknown>[]; meta: unknown }>('/twin/projects')
    return response.data.map(transformTwinProject)
  },
  getProject: async (projectId: string): Promise<TwinProject> => {
    const response = await apiRequest<{ data: Record<string, unknown>; meta: unknown }>(`/twin/projects/${projectId}`)
    return transformTwinProject(response.data)
  },
  createProject: (data: { name: string; description?: string; metadata?: Record<string, unknown> }) =>
    apiRequest<TwinProject>('/twin/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateProject: (projectId: string, data: Partial<{ name: string; description?: string; metadata?: Record<string, unknown> }>) =>
    apiRequest<TwinProject>(`/twin/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  deleteProject: (projectId: string) =>
    apiRequest<void>(`/twin/projects/${projectId}`, { method: 'DELETE' }),

  // File upload
  uploadCAD: (projectId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return fetch(`${import.meta.env.VITE_API_URL || '/api'}/twin/projects/${projectId}/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(res => {
      if (!res.ok) throw new Error('Failed to upload CAD file')
      return res.json() as Promise<{ message: string; conversionId?: string }>
    })
  },

  // Conversion status
  getConversionStatus: (projectId: string) =>
    apiRequest<TwinConversionStatus>(`/twin/projects/${projectId}/status`),
  getConversionHistory: (projectId: string) =>
    apiRequest<TwinConversionStatus[]>(`/twin/projects/${projectId}/conversions`),

  // Model data
  getModelUrl: (projectId: string) =>
    apiRequest<{ url: string; format: string }>(`/twin/projects/${projectId}/model`),
  getModelMetadata: (projectId: string) =>
    apiRequest<TwinModelMetadata>(`/twin/projects/${projectId}/metadata`),
  getPartInfo: (projectId: string, partId: string) =>
    apiRequest<{ id: string; name: string; properties: Record<string, unknown> }>(
      `/twin/projects/${projectId}/parts/${partId}`
    ),

  // Sample documents
  getSample: async (sampleId: string): Promise<{ document?: TwinSampleDocument }> => {
    const response = await apiRequest<Record<string, unknown>>(`/twin/samples/${sampleId}`)
    // Extract document metadata from sample response if present
    if (response.document) {
      return {
        document: {
          title: (response.document as Record<string, unknown>).title as string,
          description: (response.document as Record<string, unknown>).description as string | null,
          language: (response.document as Record<string, unknown>).language as string,
          pages: (response.document as Record<string, unknown>).pages as number,
          url: (response.document as Record<string, unknown>).url as string,
          file_size_bytes: (response.document as Record<string, unknown>).file_size_bytes as number,
        },
      }
    }
    return {}
  },
  getSampleDocumentUrl: (sampleId: string): string =>
    `${import.meta.env.VITE_API_URL || '/api'}/twin/samples/${sampleId}/document.pdf`,
}

// ===========================================
// CPQ RULES API
// ===========================================

export interface CpqRuleCondition {
  field: 'discount_percent' | 'margin_percent' | 'line_total' | 'doc_total' | 'quantity'
  operator: '>' | '<' | '>=' | '<=' | '=' | '!='
  value: number
  unit?: string
}

export interface CpqApprovalConfig {
  approverRoles?: string[]
  approverUserIds?: string[]
  escalationTimeMinutes?: number
  escalateToRoles?: string[]
}

export interface CpqRule {
  id: string
  name: string
  description: string | null
  ruleType: 'max_discount' | 'min_margin' | 'max_value' | 'min_value' | 'blocked_product' | 'custom'
  condition: CpqRuleCondition
  action: 'warn' | 'require_approval' | 'block'
  approvalConfig: CpqApprovalConfig | null
  appliesToProductGroups: number[] | null
  appliesToUserRoles: string[] | null
  excludeProductCodes: string[] | null
  priority: number
  isActive: boolean
  createdById: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface CpqRuleInput {
  name: string
  description?: string
  ruleType: CpqRule['ruleType']
  condition: CpqRuleCondition
  action: CpqRule['action']
  approvalConfig?: CpqApprovalConfig
  appliesToProductGroups?: number[] | null
  appliesToUserRoles?: string[] | null
  excludeProductCodes?: string[] | null
  priority?: number
  isActive?: boolean
}

export const cpqRulesApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<CpqRule>>(`/cpq/rules${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<CpqRule>(`/cpq/rules/${id}`),
  create: (data: CpqRuleInput) =>
    apiRequest<CpqRule>('/cpq/rules', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<CpqRuleInput>) =>
    apiRequest<CpqRule>(`/cpq/rules/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: string) => apiRequest<void>(`/cpq/rules/${id}`, { method: 'DELETE' }),
  toggle: (id: string) => apiRequest<CpqRule>(`/cpq/rules/${id}/toggle`, { method: 'POST' }),
  reorder: (rules: Array<{ id: string; priority: number }>) =>
    apiRequest<{ success: boolean }>('/cpq/rules/reorder', {
      method: 'POST',
      body: JSON.stringify({ rules }),
    }),
}

// ===========================================
// CPQ CALCULATE API
// ===========================================

export interface CpqLineItemInput {
  productId?: string
  itemCode?: string
  quantity: number
  listPrice?: number
  offerPrice?: number
  discountPercent?: number
  costPrice?: number
}

export interface CpqViolation {
  ruleId: string
  ruleName: string
  ruleType: string
  action: 'warn' | 'require_approval' | 'block'
  field: string
  threshold: number
  actualValue: number
  unit: string
  message: string
  approvalConfig?: CpqApprovalConfig
}

export interface CpqLineCalculationResult {
  listPrice: number
  offerPrice: number
  discountPercent: number
  lineTotal: number
  marginPercent?: number
  violations: CpqViolation[]
  requiresApproval: boolean
  isBlocked: boolean
}

export interface CpqCalculatedLine {
  productId?: string
  itemCode?: string
  itemDescription: string
  quantity: number
  listPrice: number
  offerPrice: number
  discountPercent: number
  lineTotal: number
  costPrice?: number
  marginPercent?: number
  violations: CpqViolation[]
}

export interface CpqQuoteCalculationResult {
  lineItems: CpqCalculatedLine[]
  subtotal: number
  discountAmount: number
  vatSum: number
  docTotal: number
  documentViolations: CpqViolation[]
  allViolations: CpqViolation[]
  requiresApproval: boolean
  isBlocked: boolean
}

export interface CpqPreviewResult {
  lineCount: number
  subtotal: number
  discountAmount: number
  vatSum: number
  docTotal: number
  hasWarnings: boolean
  requiresApproval: boolean
  isBlocked: boolean
  violationSummary: {
    total: number
    warnings: number
    approvalRequired: number
    blocked: number
  }
  violations: Array<{
    ruleId: string
    ruleName: string
    action: string
    message: string
  }>
}

export const cpqCalculateApi = {
  calculateLine: (data: {
    productId?: string
    itemCode?: string
    quantity: number
    requestedDiscountPercent?: number
    requestedOfferPrice?: number
    costPrice?: number
  }) =>
    apiRequest<CpqLineCalculationResult>('/cpq/calculate/line', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  calculateQuote: (data: {
    lineItems: CpqLineItemInput[]
    documentDiscountPercent?: number
  }) =>
    apiRequest<CpqQuoteCalculationResult>('/cpq/calculate/quote', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  validateConversion: (quoteId: string, lineItems: CpqLineItemInput[]) =>
    apiRequest<{
      canConvert: boolean
      blockingViolations: CpqViolation[]
      warnings: CpqViolation[]
    }>('/cpq/calculate/validate-conversion', {
      method: 'POST',
      body: JSON.stringify({ quoteId, lineItems }),
    }),
  preview: (data: {
    lineItems: CpqLineItemInput[]
    documentDiscountPercent?: number
  }) =>
    apiRequest<CpqPreviewResult>('/cpq/calculate/preview', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

// ===========================================
// CPQ APPROVALS API
// ===========================================

export interface CpqApprovalRequest {
  id: string
  requestNumber: string
  quoteId: string
  violatedRuleId: string
  violationDetails: {
    field: string
    threshold: number
    actualValue: number
    unit: string
    lineItemId?: string
    lineItemDescription?: string
    message: string
  }
  requestedById: string
  assignedToId: string | null
  status: 'pending' | 'approved' | 'rejected' | 'escalated' | 'expired'
  decidedById: string | null
  decidedAt: string | null
  decisionNotes: string | null
  escalatedAt: string | null
  escalatedToId: string | null
  escalationReason: string | null
  expiresAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CpqApprovalRequestWithDetails extends CpqApprovalRequest {
  quoteNumber?: string
  quoteDocTotal?: string | null
  quoteStatus?: string
  ruleName?: string
  ruleType?: string
  requestedBy?: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string
  }
  assignedTo?: {
    id: string
    firstName: string | null
    lastName: string | null
    email: string
  }
}

export interface CpqApprovalStats {
  pending: number
  approvedToday: number
  rejectedToday: number
}

export const cpqApprovalsApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<CpqApprovalRequestWithDetails>>(`/cpq/approvals${query ? `?${query}` : ''}`)
  },
  getPending: () => apiRequest<CpqApprovalRequestWithDetails[]>('/cpq/approvals/pending'),
  get: (id: string) => apiRequest<CpqApprovalRequestWithDetails>(`/cpq/approvals/${id}`),
  getForQuote: (quoteId: string) =>
    apiRequest<CpqApprovalRequestWithDetails[]>(`/cpq/approvals/quote/${quoteId}`),
  approve: (id: string, notes?: string) =>
    apiRequest<CpqApprovalRequest>(`/cpq/approvals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),
  reject: (id: string, notes: string) =>
    apiRequest<CpqApprovalRequest>(`/cpq/approvals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),
  escalate: (id: string, escalateToId: string, reason?: string) =>
    apiRequest<CpqApprovalRequest>(`/cpq/approvals/${id}/escalate`, {
      method: 'POST',
      body: JSON.stringify({ escalateToId, reason }),
    }),
  assign: (id: string, assigneeId: string) =>
    apiRequest<CpqApprovalRequest>(`/cpq/approvals/${id}/assign`, {
      method: 'POST',
      body: JSON.stringify({ assigneeId }),
    }),
  getStats: () => apiRequest<CpqApprovalStats>('/cpq/approvals/stats'),
}

// ===========================================
// PDF IMPORT API (CPQ Module)
// ===========================================

export interface PDFExtractedLineItem {
  position: number
  articleNumber: string | null
  description: string
  quantity: number
  unit: string | null
  unitPrice: number
  lineTotal: number
  matchedProductId: string | null
  matchedItemCode: string | null
  matchedItemName: string | null
  confidence: number
  matchType: 'exact_code' | 'partial_code' | 'description' | 'fuzzy' | 'none'
}

export interface PDFExtractionResult {
  success: boolean
  extracted: {
    header: {
      orderNumber: string | null
      orderDate: string | null
      deliveryDate: string | null
      customerReference: string | null
    }
    customer: {
      name: string | null
      address: string | null
      matchedAccountId: string | null
      matchedCardCode: string | null
      confidence: number
      matchType: string
    }
    addresses: {
      shipping: string | null
      billing: string | null
    }
    lineItems: PDFExtractedLineItem[]
    totals: {
      netTotal: number | null
      vatRate: number | null
      vatAmount: number | null
      grossTotal: number | null
      currency: string
    }
    terms: {
      paymentTerms: string | null
      shippingTerms: string | null
      incoterms: string | null
    }
  }
  matchSummary: {
    totalLineItems: number
    matchedLineItems: number
    unmatchedLineItems: number
    productConfidence: number
    customerConfidence: number
    customerMatched: boolean
  }
  pdfFilename?: string
  error?: string
}

export interface CreateQuoteFromPDFResult {
  success: boolean
  quote: QuoteWithRelations
  lineItems: QuoteLineItemRecord[]
  proxyProducts: ProxyProductRecord[]
  summary: {
    totalLineItems: number
    matchedProducts: number
    proxyProductsCreated: number
    pdfStorageKey: string | null
  }
}

export interface ProductSuggestion {
  id: string
  itemCode: string
  itemName: string
  shortDescription: string | null
  defaultPrice: string | null
  confidence: number
}

export interface AccountSuggestion {
  id: string
  name: string
  externalId: string | null
  type: string | null
  confidence: number
}

export const pdfImportApi = {
  parsePDF: (pdfBase64: string, filename?: string) =>
    apiRequest<PDFExtractionResult>('/orders/parse-pdf', {
      method: 'POST',
      body: JSON.stringify({ pdfBase64, filename }),
    }),

  createQuoteFromPDF: (data: {
    pdfBase64: string
    filename?: string
    accountId?: string | null
    contactId?: string | null
    customerName?: string | null
    lineItems: Array<{
      position: number
      articleNumber: string | null
      description: string
      quantity: number
      unit: string | null
      unitPrice: number
      lineTotal: number
      matchedProductId: string | null
      matchedItemCode: string | null
      confidence: number
      matchType: string
    }>
    totals: {
      netTotal: number | null
      vatRate: number | null
      vatAmount: number | null
      grossTotal: number | null
      currency: string
    }
    header: {
      orderNumber: string | null
      orderDate: string | null
      deliveryDate: string | null
      customerReference: string | null
    }
  }) =>
    apiRequest<CreateQuoteFromPDFResult>('/orders/create-quote-from-pdf', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getProductSuggestions: (articleNumber: string | null, description: string, limit?: number) =>
    apiRequest<ProductSuggestion[]>('/orders/product-suggestions', {
      method: 'POST',
      body: JSON.stringify({ articleNumber, description, limit }),
    }),

  getAccountSuggestions: (customerName: string | null, customerAddress: string | null, limit?: number) =>
    apiRequest<AccountSuggestion[]>('/orders/account-suggestions', {
      method: 'POST',
      body: JSON.stringify({ customerName, customerAddress, limit }),
    }),
}

// ===========================================
// PROXY PRODUCTS API (CPQ Module)
// ===========================================

import type { ProxyProductRecord, ProxyProductRawData } from '../../shared/types'

export interface ProxyProductWithRelations extends ProxyProductRecord {
  resolvedToProduct?: { id: string; itemCode: string; itemName: string } | null
  resolvedBy?: { id: string; firstName: string | null; lastName: string | null; email: string } | null
}

export const proxyProductsApi = {
  list: (params?: ListParams) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (value) searchParams.set(key, value)
      }
    }
    const query = searchParams.toString()
    return apiRequest<PaginatedResponse<ProxyProductWithRelations>>(`/proxy-products${query ? `?${query}` : ''}`)
  },
  get: (id: string) => apiRequest<ProxyProductWithRelations>(`/proxy-products/${id}`),
  search: (q: string) =>
    apiRequest<ProxyProductWithRelations[]>(`/proxy-products/search?q=${encodeURIComponent(q)}`),
  resolve: (id: string, resolvedToProductId: string, resolutionNotes?: string) =>
    apiRequest<ProxyProductWithRelations>(`/proxy-products/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ resolvedToProductId, resolutionNotes }),
    }),
  reject: (id: string, reason?: string) =>
    apiRequest<ProxyProductWithRelations>(`/proxy-products/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  unresolve: (id: string) =>
    apiRequest<ProxyProductWithRelations>(`/proxy-products/${id}/unresolve`, {
      method: 'POST',
    }),
  getStats: () =>
    apiRequest<{
      total: number
      unresolved: number
      matched: number
      rejected: number
      createdInErp: number
    }>('/proxy-products/stats'),
}

export type { ProxyProductRecord, ProxyProductRawData }

// ===========================================
// SAP INTEGRATION API
// ===========================================

export interface SapConfig {
  id: string
  erpType: string
  serviceLayerUrl: string | null
  companyDb: string | null
  username: string | null
  hasPassword: boolean
  productSyncEnabled: boolean
  productSyncFrequency: string
  productPriceLists: number[] | null
  productLastSyncAt: string | null
  productLastSyncStatus: string | null
  productLastSyncCount: number | null
  productLastSyncError: string | null
  orderPushEnabled: boolean
  orderPushTriggerStatus: string
  orderLastPushAt: string | null
  orderLastPushStatus: string | null
  connectionStatus: string
  lastConnectionTestAt: string | null
  lastConnectionError: string | null
  createdAt: string
  updatedAt: string
}

export interface SapConfigUpdate {
  erpType?: string
  serviceLayerUrl?: string | null
  companyDb?: string | null
  username?: string | null
  password?: string | null
  productSyncEnabled?: boolean
  productSyncFrequency?: string
  productPriceLists?: number[] | null
  orderPushEnabled?: boolean
  orderPushTriggerStatus?: string
}

export interface SapSyncLog {
  id: string
  syncType: string
  direction: string
  status: string
  recordsProcessed: number
  recordsCreated: number
  recordsUpdated: number
  recordsFailed: number
  errorMessage: string | null
  startedAt: string
  completedAt: string | null
  durationMs: number | null
  triggeredBy: string | null
}

export interface SapStatus {
  config: {
    connectionStatus: string
    lastConnectionTest: string | null
    productSync: {
      enabled: boolean
      frequency: string
      lastSync: string | null
      lastStatus: string | null
      lastCount: number | null
      lastError: string | null
    }
    orderPush: {
      enabled: boolean
      triggerStatus: string
      lastPush: string | null
      lastStatus: string | null
    }
  }
  recentLogs: SapSyncLog[]
}

export interface SapTestResult {
  success: boolean
  message: string
  version?: string
  testedAt: string
}

export interface SapSyncResult {
  success: boolean
  message: string
  result?: {
    recordsProcessed?: number
    recordsCreated?: number
    recordsUpdated?: number
    recordsFailed?: number
  }
}

export const sapIntegrationApi = {
  getConfig: () => apiRequest<SapConfig>('/integrations/sap/config'),

  updateConfig: (data: SapConfigUpdate) =>
    apiRequest<SapConfig>('/integrations/sap/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  testConnection: (tempCredentials?: { serviceLayerUrl: string; companyDb: string; username: string; password: string }) =>
    apiRequest<SapTestResult>('/integrations/sap/test', {
      method: 'POST',
      body: JSON.stringify(tempCredentials || {}),
    }),

  getStatus: () => apiRequest<SapStatus>('/integrations/sap/status'),

  getLogs: (params?: { page?: number; limit?: number; syncType?: string; status?: string }) => {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.syncType) searchParams.set('syncType', params.syncType)
    if (params?.status) searchParams.set('status', params.status)
    const query = searchParams.toString()
    return apiRequest<{ logs: SapSyncLog[]; page: number; limit: number }>(`/integrations/sap/logs${query ? `?${query}` : ''}`)
  },

  getLogDetail: (id: string) => apiRequest<SapSyncLog>(`/integrations/sap/logs/${id}`),

  getPriceLists: () =>
    apiRequest<{ priceLists: Array<{ code: number; name: string }> }>('/integrations/sap/price-lists'),

  getItemGroups: () =>
    apiRequest<{ itemGroups: Array<{ code: number; name: string }> }>('/integrations/sap/item-groups'),

  syncProducts: (full: boolean = false) =>
    apiRequest<SapSyncResult>('/integrations/sap/sync/products', {
      method: 'POST',
      body: JSON.stringify({ full }),
    }),

  pushOrders: () =>
    apiRequest<SapSyncResult>('/integrations/sap/sync/orders', {
      method: 'POST',
    }),

  pushOrder: (orderId: string) =>
    apiRequest<{ success: boolean; message: string; sapDocEntry?: number; sapDocNum?: number }>(`/integrations/sap/sync/orders/${orderId}`, {
      method: 'POST',
    }),

  retryOrderPush: (orderId: string) =>
    apiRequest<{ success: boolean; message: string; sapDocEntry?: number; sapDocNum?: number }>(`/integrations/sap/sync/orders/${orderId}/retry`, {
      method: 'POST',
    }),
}
